import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Input, Select, Space } from 'antd';
import MatchRateBadge from './MatchRateBadge';
import {
  amountColumns,
  pickAmounts,
  renderAmountSummary,
} from './payrollAmountColumns';

const { Search } = Input;

function fmt(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('sr-RS', { maximumFractionDigits: 2 });
}

function diffClass(diff) {
  if (diff == null) return '';
  if (Math.abs(diff) < 0.01) return '';
  return diff > 0 ? 'text-blue-600' : 'text-red-600';
}

export default function ShadowDetailedReport({ items, layers }) {
  const { t } = useTranslation(['forms']);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('with-diffs');

  const rows = useMemo(() => {
    const filtered = (items || []).filter(
      (it) => it.input?.calcType !== 'OTHER_PAYMENT'
    );
    return filtered.map((it) => {
      const d = it.shadowDiff || {};
      const diffCount =
        (d.diffs?.length || 0) +
        (d.componentDiffs?.length || 0) +
        (d.otherPaymentDiffs?.length || 0) +
        (d.deductionDiffs?.length || 0);
      return {
        key: it._id,
        employeeName: it.employeeName || '',
        calcType: it.input?.calcType,
        ...pickAmounts(it.calculation),
        diffCount,
        match: d.match === true && diffCount === 0,
        summaryDiffs: d.diffs || [],
        componentDiffs: d.componentDiffs || [],
        otherPaymentDiffs: d.otherPaymentDiffs || [],
        deductionDiffs: d.deductionDiffs || [],
      };
    });
  }, [items]);

  const visibleRows = useMemo(() => {
    let r = rows;
    if (filter === 'with-diffs') r = r.filter((x) => !x.match);
    else if (filter === 'matched') r = r.filter((x) => x.match);
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.employeeName.toLowerCase().includes(q));
    }
    return r;
  }, [rows, filter, search]);

  const columns = [
    {
      title: t('employee', { ns: 'forms' }),
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (name, r) => (
        <span>
          {name}
          {r.match && (
            <Tag color="green" className="ml-2">
              OK
            </Tag>
          )}
          {!r.match && r.diffCount > 0 && (
            <Tag color="orange" className="ml-2">
              {r.diffCount} {t('diffs', { ns: 'forms', defaultValue: 'diff' })}
            </Tag>
          )}
        </span>
      ),
    },
    ...amountColumns(t),
  ];

  return (
    <div>
      {layers && (
        <Space className="mb-3" size="large">
          <span>
            <strong>Summary:</strong>{' '}
            <MatchRateBadge rate={layers.summary} />
          </span>
          <span>
            <strong>Komponente:</strong>{' '}
            <MatchRateBadge rate={layers.components} />
          </span>
          <span>
            <strong>Prevoz:</strong> <MatchRateBadge rate={layers.prevoz} />
          </span>
          <span>
            <strong>Obustave:</strong>{' '}
            <MatchRateBadge rate={layers.obustave} />
          </span>
        </Space>
      )}

      <Space className="mb-3 w-full justify-between" align="center">
        <Search
          placeholder={t('searchEmployee', {
            ns: 'forms',
            defaultValue: 'Pretraži zaposlenog',
          })}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          value={filter}
          onChange={setFilter}
          style={{ width: 200 }}
          options={[
            {
              value: 'with-diffs',
              label: t('showDiffsOnly', {
                ns: 'forms',
                defaultValue: 'Samo razlike',
              }),
            },
            {
              value: 'matched',
              label: t('showMatchedOnly', {
                ns: 'forms',
                defaultValue: 'Samo OK',
              }),
            },
            {
              value: 'all',
              label: t('showAll', { ns: 'forms', defaultValue: 'Svi' }),
            },
          ]}
        />
      </Space>

      <Table
        dataSource={visibleRows}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        summary={() => (
          // leadingCells=1 — expandable dodaje kolonu na pocetak
          renderAmountSummary({
            t,
            rows,
            visibleRows,
            leadingCells: 1,
          })
        )}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (r) => <EmployeeDiffDetail row={r} />,
          rowExpandable: (r) => r.diffCount > 0,
        }}
      />
    </div>
  );
}

function EmployeeDiffDetail({ row }) {
  const { t } = useTranslation(['forms']);
  return (
    <div className="grid grid-cols-1 gap-4 p-2 md:grid-cols-2">
      <DiffSection
        title={t('summaryDiffs', {
          ns: 'forms',
          defaultValue: 'Summary razlike',
        })}
        rows={row.summaryDiffs}
        renderHeader={
          <>
            <th className="px-2 py-1 text-left">Polje</th>
            <th className="px-2 py-1 text-right">Naš</th>
            <th className="px-2 py-1 text-right">AB Soft</th>
            <th className="px-2 py-1 text-right">Razlika</th>
          </>
        }
        renderRow={(d) => (
          <>
            <td className="px-2 py-1">{d.field}</td>
            <td className="px-2 py-1 text-right">{fmt(d.ours)}</td>
            <td className="px-2 py-1 text-right">{fmt(d.theirs)}</td>
            <td className={`px-2 py-1 text-right ${diffClass(d.diff)}`}>
              {d.diff != null ? (d.diff > 0 ? '+' : '') + fmt(d.diff) : '—'}
            </td>
          </>
        )}
      />
      <DiffSection
        title={t('componentDiffs', {
          ns: 'forms',
          defaultValue: 'Komponente (Dnevnik zarada)',
        })}
        rows={row.componentDiffs}
        renderHeader={
          <>
            <th className="px-2 py-1 text-left">Šifra</th>
            <th className="px-2 py-1 text-left">Naziv</th>
            <th className="px-2 py-1 text-right">Naš</th>
            <th className="px-2 py-1 text-right">AB Soft</th>
            <th className="px-2 py-1 text-right">Razlika</th>
          </>
        }
        renderRow={(d) => (
          <>
            <td className="px-2 py-1 font-mono">{d.earningCode}</td>
            <td className="px-2 py-1">
              {d.name}
              {d.reason && (
                <Tag
                  color={d.reason === 'MISSING_IN_ENGINE' ? 'red' : 'orange'}
                  className="ml-2"
                >
                  {d.reason}
                </Tag>
              )}
            </td>
            <td className="px-2 py-1 text-right">{fmt(d.ours)}</td>
            <td className="px-2 py-1 text-right">{fmt(d.theirs)}</td>
            <td className={`px-2 py-1 text-right ${diffClass(d.diff)}`}>
              {d.diff != null ? (d.diff > 0 ? '+' : '') + fmt(d.diff) : '—'}
            </td>
          </>
        )}
      />
      <DiffSection
        title={t('prevozDiffs', {
          ns: 'forms',
          defaultValue: 'Prevoz',
        })}
        rows={row.otherPaymentDiffs}
        renderHeader={
          <>
            <th className="px-2 py-1 text-left">Polje</th>
            <th className="px-2 py-1 text-right">Naš</th>
            <th className="px-2 py-1 text-right">AB Soft</th>
            <th className="px-2 py-1 text-right">Razlika</th>
          </>
        }
        renderRow={(d) => (
          <>
            <td className="px-2 py-1">
              {d.field}
              {d.reason && (
                <Tag
                  color={d.reason === 'MISSING_IN_ENGINE' ? 'red' : 'orange'}
                  className="ml-2"
                >
                  {d.reason}
                </Tag>
              )}
            </td>
            <td className="px-2 py-1 text-right">{fmt(d.ours)}</td>
            <td className="px-2 py-1 text-right">{fmt(d.theirs)}</td>
            <td className={`px-2 py-1 text-right ${diffClass(d.diff)}`}>
              {d.diff != null ? (d.diff > 0 ? '+' : '') + fmt(d.diff) : '—'}
            </td>
          </>
        )}
      />
      <DiffSection
        title={t('deductionDiffs', {
          ns: 'forms',
          defaultValue: 'Obustave',
        })}
        rows={row.deductionDiffs}
        renderHeader={
          <>
            <th className="px-2 py-1 text-left">Šifra</th>
            <th className="px-2 py-1 text-right">Naš</th>
            <th className="px-2 py-1 text-right">AB Soft</th>
            <th className="px-2 py-1 text-right">Razlika</th>
          </>
        }
        renderRow={(d) => (
          <>
            <td className="px-2 py-1 font-mono">
              {d.deductionCode}
              {d.reason && (
                <Tag
                  color={d.reason === 'MISSING_IN_ENGINE' ? 'red' : 'orange'}
                  className="ml-2"
                >
                  {d.reason}
                </Tag>
              )}
            </td>
            <td className="px-2 py-1 text-right">{fmt(d.ours)}</td>
            <td className="px-2 py-1 text-right">{fmt(d.theirs)}</td>
            <td className={`px-2 py-1 text-right ${diffClass(d.diff)}`}>
              {d.diff != null ? (d.diff > 0 ? '+' : '') + fmt(d.diff) : '—'}
            </td>
          </>
        )}
      />
    </div>
  );
}

function DiffSection({ title, rows, renderHeader, renderRow }) {
  return (
    <div className="min-w-0 rounded border border-gray-200 p-2">
      <h5 className="mb-2 font-medium">
        {title}{' '}
        <span className="text-xs text-gray-500">({rows?.length || 0})</span>
      </h5>
      {rows?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-xs">
            <thead className="bg-gray-50">
              <tr>{renderHeader}</tr>
            </thead>
            <tbody>
              {rows.map((d, i) => (
                <tr key={i} className="border-t">
                  {renderRow(d)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="text-xs text-gray-400">bez razlike</span>
      )}
    </div>
  );
}
